// client/src/pages/Admin/ResourceManager/hooks/usePagination.ts
import { useState, useMemo, useCallback } from 'react';
import { Resource } from '../../../../shared/models/Resource';
import { DEFAULT_PAGE_SIZE, MAX_VISIBLE_PAGES } from '../constants/resource.constants';

export const usePagination = (items: Resource[], initialPageSize: number = DEFAULT_PAGE_SIZE) => {
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(initialPageSize);

    const totalPages = useMemo(() => {
        return Math.ceil(items.length / pageSize);
    }, [items, pageSize]);

    const paginatedItems = useMemo((): Resource[] => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return items.slice(startIndex, endIndex);
    }, [items, currentPage, pageSize]);

    const startIndex = (currentPage - 1) * pageSize + 1;
    const endIndex = Math.min(currentPage * pageSize, items.length);

    const pageNumbers = useMemo(() => {
        const pages: (number | string)[] = [];
        const halfVisible = Math.floor(MAX_VISIBLE_PAGES / 2);

        if (totalPages <= MAX_VISIBLE_PAGES) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= halfVisible + 1) {
                for (let i = 1; i <= MAX_VISIBLE_PAGES - 2; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - halfVisible) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - (MAX_VISIBLE_PAGES - 3); i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            }
        }

        return pages;
    }, [currentPage, totalPages]);

    const goToPage = useCallback((page: number) => {
        setCurrentPage(Math.max(1, Math.min(totalPages, page)));
    }, [totalPages]);

    const goToFirstPage = useCallback(() => setCurrentPage(1), []);
    const goToLastPage = useCallback(() => setCurrentPage(totalPages), [totalPages]);
    const goToPreviousPage = useCallback(() => setCurrentPage(prev => Math.max(1, prev - 1)), []);
    const goToNextPage = useCallback(() => setCurrentPage(prev => Math.min(totalPages, prev + 1)), [totalPages]);

    const changePageSize = useCallback((size: number) => {
        setPageSize(size);
        setCurrentPage(1);
    }, []);

    const handlePageSizeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        changePageSize(Number(e.target.value));
    }, [changePageSize]);

    const resetPagination = useCallback(() => {
        setCurrentPage(1);
    }, []);

    return {
        currentPage,
        pageSize,
        totalPages,
        paginatedItems,
        startIndex,
        endIndex,
        pageNumbers,
        goToPage,
        goToFirstPage,
        goToLastPage,
        goToPreviousPage,
        goToNextPage,
        handlePageSizeChange,
        resetPagination,
        changePageSize,
        resetPage: resetPagination
    };
};